"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTransition } from "react";
import { isCrossZoneHref } from "@/lib/zones";
import { BrandMark } from "./brand-mark";

type Props = {
	href: string;
	label: string;
};

/**
 * The navbar wordmark.
 *
 * Two separate reasons a tap here used to produce no visible response, which
 * together made it the most rage-clicked element in the product:
 *
 * 1. On `/id` and `/en` the destination IS the current page, so the navigation
 *    resolved to nothing at all.
 * 2. Everywhere else it was a plain `<Link>` into a `force-dynamic` page. The
 *    App Router keeps the outgoing page painted until the new RSC payload
 *    arrives, and with no `loading.tsx` and no route-progress bar that is 3-5s
 *    (measured mobile LCP p75) of a completely unchanged screen.
 *
 * Both are answered here rather than styled around: when we are already at the
 * destination the tap scrolls to the top — a real, instant, visible response
 * instead of a dropped event — and otherwise the navigation runs inside a
 * transition so the control can dim while it is in flight. `active:` covers the
 * first ~16ms before either of those can land, which is the window where touch
 * users decide whether a control is broken (`hover:` does not exist for them).
 *
 * In flight it pulses rather than showing `cursor-wait` (2026-10-14 seeker RCA:
 * `a.cursor-wait` drew rage clicks — the cursor reads as "blocked", and touch
 * users never see it at all).
 */
export function WordmarkLink({ href, label }: Props) {
	const pathname = usePathname();
	const router = useRouter();
	const [navigating, startNavTransition] = useTransition();

	const isCurrent = pathname === href;
	// `/{locale}` is a zone hop for a signed-in visitor (the apex 302s it to
	// app.lokerdollar.com/{locale}/seeker), so a soft navigation's RSC fetch
	// follows a cross-origin redirect and fails ("Failed to fetch RSC
	// payload"). Cross-zone → no prefetch and a plain document navigation.
	const crossZone = isCrossZoneHref(href);

	return (
		<Link
			href={href}
			prefetch={crossZone ? false : undefined}
			aria-current={isCurrent ? "page" : undefined}
			aria-busy={navigating || undefined}
			onClick={(e) => {
				// Leave modified clicks to the browser so "open in new tab" works.
				if (
					e.metaKey ||
					e.ctrlKey ||
					e.shiftKey ||
					e.altKey ||
					e.button !== 0
				) {
					return;
				}
				e.preventDefault();
				if (isCurrent) {
					window.scrollTo({ top: 0, behavior: "smooth" });
					return;
				}
				if (crossZone) {
					window.location.assign(href);
					return;
				}
				// Already on its way: a re-tap would only restart the same push.
				if (navigating) return;
				startNavTransition(() => {
					router.push(href);
				});
			}}
			className={`inline-flex min-h-11 shrink-0 items-center whitespace-nowrap text-lg font-bold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-xl ${
				navigating
					? "text-primary/60 motion-safe:animate-pulse"
					: "text-primary hover:text-primary/90 active:text-primary/70"
			}`}
		>
			{/* Below `sm` the header has ~29px of slack at 375px, so the phone
			    header shows the mark alone and keeps the label for screen
			    readers; from `sm` up the text wordmark stays (PR #7958 measured
			    no room there for mark + label at the CI nav set). */}
			<BrandMark className="size-8 sm:hidden" />
			<span className="sr-only sm:not-sr-only">{label}</span>
		</Link>
	);
}
